import React, { useState, useMemo } from 'react';
import { NoDataPlaceHolderTableRow } from 'src/components/BeaconV2Components/common/NoDataPlaceHolder/NoDataPlaceHolder';
import EntityTable from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTable';
import { EmptyAdjustmentContentScore } from 'src/components/SvgIcons';
import { StyledTrashIcon } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/components/ui/DeleteIcon';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import saveAs from 'file-saver';
import AppSwitch from 'src/components/common/AppSwitch';
import { AppTableColumnDef } from '@stackline/ui';
import { getAppName } from 'src/utils/app';
import { useLocation } from 'src/utils/Hooks';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import {
  useListAlerts,
  useToggleAlertStatus,
  useDeleteAlert
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/hooks/useAlertsApi';
import { AlertsTabsPath } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/utils';
import { AlertsListItemType } from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Alerts/schema/ApiSchema';
import { styled } from '@mui/material/styles';
import colors from 'src/utils/colors';

// Styled clickable alert name component
const ClickableAlertName = styled('span')(({ theme }) => ({
  cursor: 'pointer !important',
  color: colors.darkBlue,
  textDecoration: 'underline',
  fontSize: '14px', // Match Text variant="body2" size
  fontFamily: theme.typography.fontFamily,
  '&:hover': {
    color: colors.blue,
    textDecoration: 'underline'
  }
}));

export const AlertTableConstants = {
  COLUMN_HEADERS: {
    ALERT_NAME: 'ALERT NAME',
    CREATED_DATE: 'CREATED DATE',
    SCOPE: 'SCOPE',
    TRIGGERED: 'TRIGGERED',
    STATUS: 'STATUS',
    DELETE: 'DELETE'
  },
  NO_DATA: {
    TITLE: 'No alerts found.',
    DESCRIPTION: 'You haven\'t created any alerts yet. Click "Create Alert" to get started.'
  }
};

export const handleCsvDownload = (formattedData: string[][], fileName = 'Alerts.csv') => {
  const escapeDoubleQuotes = (field: string) => {
    return `"${field.replace(/"/g, '""')}"`;
  };

  const csvContent = formattedData.map((row) => row.map(escapeDoubleQuotes).join(',')).join('\n');

  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob as Blob, fileName);
  } catch (e) {
    console.error('Error exporting alerts:', e);
  }
};

// Create spacer columns for consistent spacing
const createSpacerColumn = (
  fieldName: string,
  width = '40px'
): Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'> => ({
  headerName: '',
  field: fieldName,
  tableColumnProps: {
    style: {
      width,
      padding: '0px',
      borderLeft: 'none',
      borderRight: 'none'
    }
  },
  valueFormatter: () => {
    return <div style={{ width }}></div>;
  }
});

const AlertTables: React.FC = () => {
  const location = useLocation();
  const { beaconNavigator } = useBeaconNavigator();

  // Determine if this is team alerts based on pathname
  const isTeamAlerts =
    location.pathname.includes(AlertsTabsPath.teamAlerts) || location.pathname.includes(AlertsTabsPath.teamAlerts);
  const { COLUMN_HEADERS } = AlertTableConstants;
  const appName = getAppName();

  // Determine query parameters based on tab and app
  const group = isTeamAlerts ? 'team' : 'self';
  const product = appName.toLowerCase(); // beacon or atlas

  const { data: alertsResponse, isLoading, error } = useListAlerts(group, product);

  const { toggleAlertStatusAsync, isPending: isToggling } = useToggleAlertStatus();
  const { deleteAlertAsync, isPending: isDeleting } = useDeleteAlert();

  // Track which specific alert is being toggled or deleted
  const [togglingAlertId, setTogglingAlertId] = useState<string | null>(null);
  const [deletingAlertId, setDeletingAlertId] = useState<string | null>(null);

  // Transform API response to table format
  const alertData = useMemo(() => {
    if (!alertsResponse?.data) return [];

    return alertsResponse.data.map((alert: AlertsListItemType) => {
      return {
        key: alert.alertId,
        alertName: alert.alertName,
        createdDate: new Date(alert.createdAt).toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }),
        scope: 'Alert', // Default scope since API doesn't provide this field
        triggered: alert.triggeredCount,
        status: alert.enabledFlag
      };
    });
  }, [alertsResponse?.data]);

  const handleStatusToggle = async (alertKey: string) => {
    // Find the current alert to get its current status
    const currentAlert = alertData.find((alert) => alert.key === alertKey);
    if (!currentAlert) return;

    // Set this alert as the one being toggled
    setTogglingAlertId(alertKey);

    try {
      // Toggle the status (opposite of current status)
      await toggleAlertStatusAsync({
        alertId: alertKey,
        enabledFlag: !currentAlert.status
      });
    } finally {
      // Clear the toggling state regardless of success/failure
      setTogglingAlertId(null);
    }
  };

  const handleDeleteAlert = async (alertKey: string) => {
    // Set this alert as the one being deleted
    setDeletingAlertId(alertKey);

    try {
      // Delete the alert
      await deleteAlertAsync({
        alertId: alertKey
      });
    } finally {
      // Clear the deleting state regardless of success/failure
      setDeletingAlertId(null);
    }
  };

  const columnDefs: (Omit<AppTableColumnDef, 'field'> & Pick<Partial<AppTableColumnDef>, 'field'>)[] = [
    {
      headerName: COLUMN_HEADERS.ALERT_NAME,
      field: 'alertName',
      tableColumnProps: {
        style: {
          width: 'auto',
          paddingRight: '16px',
          minWidth: '300px'
        }
      },
      valueFormatter: (value: string, row: any) => {
        const handleAlertNameClick = () => {
          const alertId = row.key;

          // Set just the pathname (without query params)
          beaconNavigator.setPathname(AlertsTabsPath.editAlert);

          // Update alertId as a search parameter (this will overwrite any existing alertId)
          beaconNavigator.updateSearchParams({ alertId: [alertId] });

          beaconNavigator.go();
        };

        return <ClickableAlertName onClick={handleAlertNameClick}>{value}</ClickableAlertName>;
      }
    },
    createSpacerColumn('spacer1'),
    {
      headerName: COLUMN_HEADERS.CREATED_DATE,
      field: 'createdDate',
      tableColumnProps: {
        style: {
          width: '120px',
          padding: '0px'
        }
      },
      valueFormatter: (value: string) => {
        return <Text variant="body2">{value}</Text>;
      }
    },
    createSpacerColumn('spacer2'),
    {
      headerName: COLUMN_HEADERS.SCOPE,
      field: 'scope',
      tableColumnProps: {
        style: {
          width: '100px',
          padding: '0px'
        }
      },
      valueFormatter: (value: string) => {
        return <Text variant="body2">{value}</Text>;
      }
    },
    createSpacerColumn('spacer3'),
    {
      headerName: COLUMN_HEADERS.TRIGGERED,
      field: 'triggered',
      tableColumnProps: {
        style: {
          width: '100px',
          padding: '0px'
        }
      },
      valueFormatter: (value: any) => {
        return <Text variant="body2">{value}</Text>;
      }
    },
    createSpacerColumn('spacer4'),
    {
      headerName: COLUMN_HEADERS.STATUS,
      field: 'status',
      tableColumnProps: {
        style: {
          width: '80px',
          padding: '0px'
        }
      },
      valueFormatter: (_: string | number, row: any) => {
        return (
          <AppSwitch
            switchProps={{
              checked: row.status,
              onChange: () => handleStatusToggle(row.key),
              disabled: togglingAlertId === row.key,
              size: 'small'
            }}
          />
        );
      }
    },
    createSpacerColumn('spacer5'),
    {
      headerName: COLUMN_HEADERS.DELETE,
      field: 'delete',
      tableColumnProps: {
        style: {
          width: '80px',
          padding: '8px',
          textAlign: 'center'
        }
      },
      valueFormatter: (_: string | number, row: any) => {
        const isCurrentlyDeleting = deletingAlertId === row.key;
        return (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <StyledTrashIcon
              disabled={isCurrentlyDeleting}
              onClick={() => !isCurrentlyDeleting && handleDeleteAlert(row.key)}
              width="20px"
              height="20px"
            />
          </div>
        );
      }
    }
  ];

  const generateCSVData = () => {
    const headers = [
      COLUMN_HEADERS.ALERT_NAME,
      COLUMN_HEADERS.CREATED_DATE,
      COLUMN_HEADERS.SCOPE,
      COLUMN_HEADERS.TRIGGERED,
      COLUMN_HEADERS.STATUS
    ];

    const rows = alertData.map((item) => [
      item.alertName,
      item.createdDate,
      item.scope,
      item.triggered.toString(),
      item.status ? 'Active' : 'Inactive'
    ]);

    return [headers, ...rows];
  };

  return (
    <Flex flexDirection="column">
      <EntityTable
        onRowClick={() => {}}
        isLoading={isLoading}
        rowData={alertData}
        columnDefs={columnDefs}
        shouldModifyColumnDefs={false}
        supportViewMore
        initialPageSize={10}
        shouldPaginateViewMore
        appTableProps={{
          sx: {
            '& th[data-field*="spacer"], & td[data-field*="spacer"]': {
              borderLeft: 'none !important',
              borderRight: 'none !important',
              borderTop: 'none !important',
              borderBottom: 'none !important'
            },
            '& thead th': {
              paddingBottom: '16px !important'
            },
            '& tbody tr': {
              cursor: 'default !important'
            }
          },
          noDataPlaceholder: (
            <NoDataPlaceHolderTableRow
              icon={<EmptyAdjustmentContentScore />}
              title={error ? 'Error loading alerts' : AlertTableConstants.NO_DATA.TITLE}
              description={error ? 'Failed to load alerts. Please try again.' : AlertTableConstants.NO_DATA.DESCRIPTION}
            />
          )
        }}
      />
    </Flex>
  );
};

export default AlertTables;
